/**
 * Regional city configurations for /lawn-care/{slug} pages.
 *
 * SEO note: These pages are written for a homeowner searching
 * "Round Rock lawn care" or "Austin bermuda watering schedule" —
 * useful-first, product-mentioned-softly. Persona = regular guy with
 * receipts, NOT branded SaaS. The Memorial Day Showcase callout is the
 * only place we lean into the TurfPulse story.
 *
 * All water-restriction copy MUST point readers at the city's official
 * source-of-truth — restrictions change, our trust depends on not
 * publishing stale data.
 */

export const CITIES = {
  'round-rock-tx': {
    name: 'Round Rock',
    state: 'TX',
    slug: 'round-rock-tx',
    headline: 'Round Rock, TX Lawn Care — what actually works on this dirt',
    subhead:
      'Bermuda fairways, blackland-clay-meets-sandy-loam soil, Stage 2 water restrictions year-round. Here\'s the no-fluff guide for the homeowner who wants a lawn that looks intentional without spending Saturdays guessing.',
    population: '~135,000',
    usda_zone: '8b',
    annual_rainfall_in: 32,
    soil_type: 'Clay-heavy in the north (Pflugerville Series, vertic), sandy loam pockets near the Edwards Aquifer transition. Water-holding capacity ranges 0.13–0.18 in/in across the city.',
    common_grass: ['Bermuda (most common, including TifTuf and Tahoma 31)', 'Zoysia (premium subdivisions)', 'St. Augustine (older shaded yards)'],
    water_restrictions: {
      stage: 'Stage 2 (year-round)',
      key_rules: [
        'No irrigation between 10:00 AM and 7:00 PM (year-round)',
        'Twice-weekly watering by address (even-ending: Sun/Thu · odd: Wed/Sat for residential)',
        'Hand-watering with hose-end shutoff allowed any time',
        'Drip irrigation allowed any day before 10 AM / after 7 PM',
        'First violation: warning. Second: $50 fine. Third+: up to $500.',
      ],
      official_link: 'https://www.roundrocktexas.gov/departments/utilities-and-environmental-services/water-conservation/',
      official_label: 'Round Rock Utilities · Water Conservation',
    },
    rebates: [
      {
        name: 'WaterSmart Smart Irrigation Controller Rebate',
        amount: 'Up to $100',
        notes: 'Wi-Fi-enabled, EPA WaterSense-certified controllers eligible (Rachio 3 + 8/16 zone qualifies).',
        link: 'https://www.roundrocktexas.gov/departments/utilities-and-environmental-services/water-conservation/',
      },
      {
        name: 'Free Conservation Kit',
        amount: 'Free',
        notes: 'Soaker hose, hose-end timer, rain gauge — request via Utility Conservation form.',
        link: 'https://www.roundrocktexas.gov/departments/utilities-and-environmental-services/water-conservation/',
      },
    ],
    showcase_callout: true, // ← only Round Rock gets the founder-lawn callout
    this_week_call: {
      headline: 'Right now in Round Rock',
      bullets: [
        'Soil temp is climbing through 65°F — your warm-season grasses are waking. First mow of the season next 7-14 days.',
        'Pre-emergent window for crabgrass closes when soil hits 70°F sustained. If you haven\'t put down prodiamine yet, this is the last call.',
        'Bermuda greens up unevenly — don\'t panic-fertilize the brown patches yet. Wait until 80% green coverage before the first heavy nitrogen pass.',
        'Watch dewpoint after May 10. Dollar spot pressure climbs fast in our humidity, especially on overwatered north-facing slopes.',
      ],
    },
    bullet_intro: [
      'You have the same soil challenges your neighbor has. The difference is whether you\'re measuring it or guessing at it.',
      'Round Rock\'s Stage 2 restrictions aren\'t a punishment — they actually align with what TAMU AgriLife recommends for Bermuda anyway (early-morning watering, twice-weekly deep soaks). The city is doing you an agronomic favor; lean into it.',
    ],
  },

  'austin-bermuda': {
    name: 'Austin',
    state: 'TX',
    slug: 'austin-bermuda',
    headline: 'Austin, TX Bermuda Lawn Care — once-a-week watering, full-quality grass',
    subhead:
      'Austin Water\'s Stage 2 restrictions cap you at one irrigation day per week. Bermuda can absolutely thrive on that schedule — if you water deep, water early, and stop guessing at the runtime.',
    population: '~975,000',
    usda_zone: '8b',
    annual_rainfall_in: 34,
    soil_type: 'Blackland Prairie clay in east Austin (Houston Black series, very high water-holding but slow infiltration). Edwards Plateau caliche in west Austin (rocky, fast-draining, alkaline).',
    common_grass: ['Bermuda (dominant in newer subdivisions, TifTuf rising fast)', 'St. Augustine (shaded yards, central + south Austin)', 'Zoysia (premium HOAs)'],
    water_restrictions: {
      stage: 'Stage 2 (since 2022, no end date)',
      key_rules: [
        'Once-per-week irrigation, day assigned by your address (Mon/Tue/Thu/Fri/Sat schedule)',
        'No irrigation between 10:00 AM and 7:00 PM',
        'Hand-held hose with shutoff: any time',
        'Drip irrigation: any day before 10 AM / after 7 PM',
        'First violation: warning. Subsequent: $475+ fines.',
      ],
      official_link: 'https://www.austintexas.gov/department/water-conservation',
      official_label: 'Austin Water · Water Conservation',
    },
    rebates: [
      {
        name: 'WaterWise Landscape Rebate',
        amount: 'Up to $3,000',
        notes: 'For converting turf to native/drought-tolerant landscape. Not for keeping your bermuda — but worth knowing about for problem zones.',
        link: 'https://www.austintexas.gov/department/water-conservation',
      },
      {
        name: 'Smart Irrigation Controller Rebate',
        amount: 'Up to $200',
        notes: 'Rachio 3 (8/16 zone) qualifies. Combine with the LCRA Smart Controller program for some service areas.',
        link: 'https://www.austintexas.gov/department/water-conservation',
      },
    ],
    showcase_callout: false,
    this_week_call: {
      headline: 'Right now in Austin',
      bullets: [
        'Once-a-week watering means your single fire HAS to count. The TAMU rule of thumb: 1.0–1.5 inches per week, in two waves if your soil is clay (Houston Black east) or one deep wave if you\'re on caliche (west).',
        'Bermuda green-up runs late April through mid-May here. Fertilize once 80% green coverage hits (avoid March pushes — you\'ll feed weeds).',
        'Austin\'s humidity from May onward is a dollar-spot factory. Mow tall (1.5–2.0" for hybrid bermuda), water early, never water at night.',
      ],
    },
    bullet_intro: [
      'Once-a-week watering is the single biggest filter for which lawn care advice actually applies to you. Most national articles assume 2–3 days/week — that math doesn\'t fit Austin Stage 2.',
      'A soil moisture probe is worth more in Austin than anywhere else in Texas. When you only get one shot per week, you cannot afford to fire on a calendar.',
    ],
  },

  'cedar-park-tx': {
    name: 'Cedar Park',
    state: 'TX',
    slug: 'cedar-park-tx',
    headline: 'Cedar Park, TX Lawn Care — Brushy Creek soils + Stage 2 reality',
    subhead:
      'Cedar Park sits on a different soil profile than Austin or Round Rock. Caliche-rich, fast-draining in pockets, clay-heavy in others — your neighbor\'s schedule probably won\'t work for you.',
    population: '~80,000',
    usda_zone: '8b',
    annual_rainfall_in: 31,
    soil_type: 'Edwards Plateau caliche in west Cedar Park (rocky, alkaline pH 7.8–8.4, fast infiltration). Limestone-derived clay loam in east. Heterogeneous — test your specific yard.',
    common_grass: ['Bermuda (subdivisions post-2000)', 'Zoysia (Brushy Creek MUD premium)', 'St. Augustine (shaded mature trees)'],
    water_restrictions: {
      stage: 'Stage 2 (Brushy Creek MUD + City of Cedar Park, year-round)',
      key_rules: [
        'Twice-weekly irrigation by address (even / odd days)',
        'No irrigation 10:00 AM – 7:00 PM',
        'Drip allowed any day outside 10–7 window',
        'Hand watering with shutoff: any time',
      ],
      official_link: 'https://www.cedarparktexas.gov/departments/water',
      official_label: 'Cedar Park · Water Department',
    },
    rebates: [
      {
        name: 'Smart Controller Rebate',
        amount: 'Varies (~$100)',
        notes: 'Through Brushy Creek MUD or City of Cedar Park, depending on service area. Confirm yours before purchase.',
        link: 'https://www.cedarparktexas.gov/departments/water',
      },
    ],
    showcase_callout: false,
    this_week_call: {
      headline: 'Right now in Cedar Park',
      bullets: [
        'Caliche soil in west Cedar Park drains fast — a typical 30-min rotor cycle can put down 0.5" but only 0.2" reaches the root zone. Catch-cup test your yard first.',
        'High soil pH (>7.5) common — if your iron supplement isn\'t showing color in 7-10 days, your pH is locking nutrients out. Sulfur or chelated micros are the fix, not more iron.',
        'May warm-up: bermuda shifts into growth gear. First mow of the season is coming if it hasn\'t already.',
      ],
    },
    bullet_intro: [
      'Cedar Park\'s soil is the most variable in the I-35 corridor. Don\'t assume the schedule that works for your buddy in Round Rock applies to your yard.',
    ],
  },

  'pflugerville-tx': {
    name: 'Pflugerville',
    state: 'TX',
    slug: 'pflugerville-tx',
    headline: 'Pflugerville, TX Lawn Care — Houston Black clay + smart watering',
    subhead:
      'Pflugerville sits on the Blackland Prairie. That clay holds water like a sponge — and seals like one when you get the runtime wrong. Here\'s the call for our zip codes.',
    population: '~70,000',
    usda_zone: '8b',
    annual_rainfall_in: 33,
    soil_type: 'Houston Black clay (vertic, very high water-holding 0.18+ in/in but slow infiltration <0.3 in/hr). Cracks dramatically when dry, becomes a bog when oversaturated.',
    common_grass: ['Bermuda', 'St. Augustine (older neighborhoods near Lake Pflugerville)', 'Zoysia'],
    water_restrictions: {
      stage: 'Stage 2 (Pflugerville Utilities, year-round)',
      key_rules: [
        'Twice-weekly irrigation by address',
        'No irrigation 10:00 AM – 7:00 PM',
        'Hand watering with shutoff: any time',
      ],
      official_link: 'https://www.pflugervilletx.gov/city-services/utilities/water-conservation',
      official_label: 'Pflugerville Utilities · Water Conservation',
    },
    rebates: [
      {
        name: 'Smart Irrigation Rebate',
        amount: 'Varies',
        notes: 'Confirm through Pflugerville Utilities — programs change yearly.',
        link: 'https://www.pflugervilletx.gov/city-services/utilities/water-conservation',
      },
    ],
    showcase_callout: false,
    this_week_call: {
      headline: 'Right now in Pflugerville',
      bullets: [
        'On Houston Black clay, the standard 30-min run is a runoff machine. Cycle-and-soak (3 × 10 min with 30-min soak between) puts the same total water down with zero waste.',
        'When your soil cracks visibly, you\'ve waited too long between fires — those cracks become preferential flow paths and the water just runs straight past the root zone next time you water.',
        'Bermuda green-up starts later on clay (May). Don\'t fertilize until 80% green or you\'re feeding poa annua.',
      ],
    },
    bullet_intro: [
      'Houston Black clay is the most-misunderstood soil in central Texas. It looks like it can\'t take water (cracks), then can\'t drain when wet. Probe-driven irrigation is the cheat code.',
    ],
  },

  'georgetown-tx': {
    name: 'Georgetown',
    state: 'TX',
    slug: 'georgetown-tx',
    headline: 'Georgetown, TX Lawn Care — Edwards Aquifer rules + warm-season grass',
    subhead:
      'Georgetown sits over the Edwards Aquifer recharge zone — water conservation isn\'t optional, it\'s civic. And the soil is rockier than your friends to the south. Here\'s the Georgetown-specific guide.',
    population: '~80,000',
    usda_zone: '8a',
    annual_rainfall_in: 32,
    soil_type: 'Edwards Plateau caliche dominant. Shallow rocky soils with limestone bedrock close to the surface in many subdivisions. Drainage fast, but root depth is constrained.',
    common_grass: ['Bermuda', 'Zoysia (premium HOAs near Sun City)', 'Buffalograss (drought-tolerant native pockets)'],
    water_restrictions: {
      stage: 'Stage 2 (Georgetown Utility Systems, year-round)',
      key_rules: [
        'Twice-weekly irrigation by address',
        'No irrigation 10:00 AM – 7:00 PM',
        'Hand watering with shutoff: any time',
        'Drip irrigation: any day outside 10–7 window',
      ],
      official_link: 'https://gus.georgetown.org/water-conservation/',
      official_label: 'Georgetown Utility Systems · Water Conservation',
    },
    rebates: [
      {
        name: 'GUS Conservation Rebates',
        amount: 'Varies',
        notes: 'Smart controllers, rain barrels, and turf conversion all eligible in different programs. Check the GUS site for current rebate cycle.',
        link: 'https://gus.georgetown.org/water-conservation/',
      },
    ],
    showcase_callout: false,
    this_week_call: {
      headline: 'Right now in Georgetown',
      bullets: [
        'Shallow root depth on caliche (sometimes <4") means your effective root zone is smaller than a typical Bermuda lawn — adjust runtime calculations down by 30–40%.',
        'Iron deficiency is rampant due to high soil pH. Foliar iron applications matter more than soil amendments here.',
        'Sun City and Wolf Ranch subdivisions tend to over-irrigate on default Rachio schedules — if your monthly bill is over $200, your runtime is probably wrong.',
      ],
    },
    bullet_intro: [
      'Georgetown is the most water-conscious city in the corridor for a reason — the aquifer recharge zone is right under your lawn. Smart watering here isn\'t just about your bill, it\'s about whether the springs flow next summer.',
    ],
  },
};

export const ALL_CITY_SLUGS = Object.keys(CITIES);
